import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  getMoneyReport,
  listTenants,
  listUsers,
  getMyAdminTenants,
  type MoneyReportItem,
  type MoneyReportTotals,
  type Tenant,
} from '@/services/adminApi';
import type { User } from '@/types/user';
import * as XLSX from 'xlsx';

export default function MoneyTab() {
  const { t } = useTranslation('admin');
  const { user: currentUser } = useAuth();
  const { tenant: currentTenant } = useTenant();

  const isSuperadmin = currentUser?.is_superadmin === true;
  const isTenantAdmin = !isSuperadmin && currentUser?.role === 'admin';

  // Filter state
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Dropdown data
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  // Report data
  const [items, setItems] = useState<MoneyReportItem[]>([]);
  const [totals, setTotals] = useState<MoneyReportTotals>({
    ai_task_cost: 0,
    ai_iterations_count: 0,
    file_processing_cost: 0,
    files_processed_count: 0,
    total_cost: 0,
  });
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load tenants
  useEffect(() => {
    const loadTenants = async () => {
      try {
        if (isSuperadmin) {
          const res = await listTenants();
          setTenants(res.tenants.map((t: Tenant) => ({ id: t.id, name: t.name })));
        } else if (isTenantAdmin) {
          const res = await getMyAdminTenants();
          setTenants(res.tenants.map((t: { tenant_id: string; name: string }) => ({ id: t.tenant_id, name: t.name })));
        }
      } catch {
        // Tenant list is non-critical
      }
    };
    loadTenants();
  }, [isSuperadmin, isTenantAdmin]);

  // Set default tenant on mount
  useEffect(() => {
    if (!selectedTenantId && currentTenant?.id) {
      setSelectedTenantId(currentTenant.id);
    }
  }, [currentTenant?.id, selectedTenantId]);

  // Load users when tenant changes
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const tenantFilter = selectedTenantId || undefined;
        const res = await listUsers(undefined, undefined, undefined, 500, 0, tenantFilter);
        setUsers(res.users);
      } catch {
        setUsers([]);
      }
    };
    loadUsers();
    setSelectedUserIds([]);
  }, [selectedTenantId]);

  // Fetch report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMoneyReport({
        tenant_id: selectedTenantId || undefined,
        user_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setItems(res.items);
      setTotals(res.totals);
      setCount(res.count);
    } catch (err) {
      setError('Failed to load cost data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, selectedUserIds, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Export to Excel
  const handleExport = () => {
    const totalsRow = {
      User: 'TOTALS',
      Username: '',
      'AI Task Cost ($)': totals.ai_task_cost,
      'AI Iterations': totals.ai_iterations_count,
      'File Processing ($)': totals.file_processing_cost,
      'Files Processed': totals.files_processed_count,
      'Total Cost ($)': totals.total_cost,
    };

    const dataRows = items.map((item) => ({
      User: item.full_name,
      Username: item.username,
      'AI Task Cost ($)': item.ai_task_cost,
      'AI Iterations': item.ai_iterations_count,
      'File Processing ($)': item.file_processing_cost,
      'Files Processed': item.files_processed_count,
      'Total Cost ($)': item.total_cost,
    }));

    const ws = XLSX.utils.json_to_sheet([totalsRow, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usage Costs');
    XLSX.writeFile(wb, `usage-costs-${dateFrom || 'all'}-${dateTo || 'all'}.xlsx`);
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedUserIds([]);
    setDateFrom('');
    setDateTo('');
    if (currentTenant?.id) {
      setSelectedTenantId(currentTenant.id);
    }
  };

  // Toggle user selection
  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const formatCost = (v: number) => `$${v.toFixed(4)}`;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('money.title')}</h2>

      {/* Filters Row */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Tenant Dropdown */}
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {isSuperadmin && <option value="">{t('money.allTenants')}</option>}
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name}
                </option>
              ))}
            </select>
          </div>

          {/* User Multi-select */}
          <div className="relative min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('money.selectUsers')}</label>
            <button
              type="button"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left bg-white hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {selectedUserIds.length > 0
                ? t('money.selectedUsers', { count: selectedUserIds.length })
                : t('money.allUsers')}
              <svg className="w-4 h-4 float-right mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {userDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="truncate">{u.full_name || u.username}</span>
                    <span className="text-gray-400 text-xs ml-auto">{u.username}</span>
                  </label>
                ))}
                {users.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400">{t('money.allUsers')}</div>
                )}
              </div>
            )}
          </div>

          {/* Date From */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('money.dateFrom')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('money.dateTo')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('money.clearFilters')}
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Export + Count */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{count} rows</span>
            <button
              onClick={handleExport}
              disabled={items.length === 0}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('money.export')}
            </button>
          </div>
        </div>
      </div>

      {/* Close user dropdown on outside click */}
      {userDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setUserDropdownOpen(false)} />
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 text-gray-500">{t('money.loading')}</div>
      )}

      {/* Table */}
      {!loading && items.length === 0 && !error && (
        <div className="text-center py-12 text-gray-400">{t('money.noData')}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('money.user')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('money.aiTaskCost')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('money.aiIterations')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('money.fileProcessingCost')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('money.filesProcessed')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('money.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Totals row */}
              <tr className="bg-emerald-50 border-b-2 border-emerald-200 font-semibold">
                <td className="px-4 py-3 text-emerald-800">{t('money.totals')}</td>
                <td className="text-right px-4 py-3 text-emerald-800">{formatCost(totals.ai_task_cost)}</td>
                <td className="text-right px-4 py-3 text-emerald-800">{totals.ai_iterations_count}</td>
                <td className="text-right px-4 py-3 text-emerald-800">{formatCost(totals.file_processing_cost)}</td>
                <td className="text-right px-4 py-3 text-emerald-800">{totals.files_processed_count}</td>
                <td className="text-right px-4 py-3 text-emerald-800">{formatCost(totals.total_cost)}</td>
              </tr>
              {/* Data rows */}
              {items.map((item) => (
                <tr key={item.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.full_name}</div>
                    <div className="text-xs text-gray-400">{item.username}</div>
                  </td>
                  <td className="text-right px-4 py-3 text-gray-700">{formatCost(item.ai_task_cost)}</td>
                  <td className="text-right px-4 py-3 text-gray-700">{item.ai_iterations_count}</td>
                  <td className="text-right px-4 py-3 text-gray-700">{formatCost(item.file_processing_cost)}</td>
                  <td className="text-right px-4 py-3 text-gray-700">{item.files_processed_count}</td>
                  <td className="text-right px-4 py-3 font-medium text-gray-900">{formatCost(item.total_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
